import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, User } from 'lucide-react';

interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  product?: string;
  date: string;
  verified: boolean;
}

interface CustomerReviewsProps {
  productId?: string;
  limit?: number;
}

const CustomerReviews: React.FC<CustomerReviewsProps> = ({
  productId,
  limit = 6
}) => {
  // Mock reviews data - in a real app, this would come from your database
  const reviews: Review[] = [
    {
      id: '1',
      name: 'Arjun Sharma',
      rating: 5,
      comment: 'Excellent quality guitar! The sound is amazing and delivery was super fast. Highly recommend Strum Music for all musical needs.',
      product: 'Yamaha F310 Acoustic Guitar',
      date: '2024-01-15',
      verified: true
    },
    {
      id: '2',
      name: 'Priya Patel',
      rating: 5,
      comment: 'Great customer service and authentic products. Bought a keyboard and it exceeded my expectations. Will definitely shop again!',
      product: 'Yamaha PSR-E373',
      date: '2024-01-10',
      verified: true
    },
    {
      id: '3',
      name: 'Rahul Kumar',
      rating: 4,
      comment: 'Good quality instruments at competitive prices. The packaging was excellent and product arrived in perfect condition.',
      product: 'Roland TD-17KVX',
      date: '2024-01-08',
      verified: true
    },
    {
      id: '4',
      name: 'Sneha Gupta',
      rating: 5,
      comment: 'Amazing experience! The team helped me choose the right guitar for my skill level. Very professional and knowledgeable.',
      product: 'Fender Player Stratocaster',
      date: '2024-01-05',
      verified: true
    },
    {
      id: '5',
      name: 'Vikram Singh',
      rating: 5,
      comment: 'Best music store in Delhi! Authentic products, great prices, and excellent after-sales support. Highly recommended!',
      product: 'Shure SM58',
      date: '2024-01-03',
      verified: true
    },
    {
      id: '6',
      name: 'Anita Reddy',
      rating: 4,
      comment: 'Very satisfied with my purchase. The rental service is also excellent - perfect for trying instruments before buying.',
      product: 'Taylor 814ce',
      date: '2024-01-01',
      verified: true
    }
  ];

  const displayedReviews = reviews.slice(0, limit);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-4 w-4 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section className="py-12 bg-white">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Don't just take our word for it - hear from musicians who trust Strum Music for their musical journey
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedReviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-lg p-6 relative"
            >
              <Quote className="h-8 w-8 text-red-200 absolute top-4 right-4" />
              
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-red-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{review.name}</h4>
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {renderStars(review.rating)}
                    </div>
                    {review.verified && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                "{review.comment}"
              </p>

              <div className="text-sm text-gray-500">
                {review.product && (
                  <p className="font-medium text-gray-600 mb-1">{review.product}</p>
                )}
                <p>{formatDate(review.date)}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Overall Rating Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-12 text-center bg-red-50 rounded-lg p-8"
        >
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="flex space-x-1">
              {renderStars(5)}
            </div>
            <span className="text-2xl font-bold text-gray-900">4.8/5</span>
          </div>
          <p className="text-gray-700 mb-2">
            <span className="font-semibold">Excellent</span> based on 500+ reviews
          </p>
          <p className="text-sm text-gray-600">
            Join thousands of satisfied musicians who trust Strum Music
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CustomerReviews;